import * as React from "react"
import type { FormEvent } from "react"
import { Head, Link, router, useForm, usePage } from "@inertiajs/react"
import { Pencil, Plus, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { SettingsLayout } from "@/components/settings/SettingsLayout"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import type { PageProps } from "@/types/inertia"

type ChecklistTemplateItem = {
  readonly id: number
  readonly title: string
  readonly position: number
  readonly due_offset_days: number
}

type ChecklistTemplateRecord = {
  readonly id: number | null
  readonly name: string
  readonly items: readonly ChecklistTemplateItem[]
  readonly tasks_count: number
}

type ClientOption = {
  readonly id: number
  readonly display_name: string
}

type ChecklistTemplateFormProps = {
  readonly title: string
  readonly checklist_template: ChecklistTemplateRecord
  readonly clients: readonly ClientOption[]
}

export default function ChecklistTemplateForm({
  title,
  checklist_template,
  clients,
}: ChecklistTemplateFormProps) {
  const { props } = usePage<PageProps<ChecklistTemplateFormProps>>()
  const errors = props.errors
  const description = "Create or edit a checklist template and the tasks it applies to a client."
  const form = useForm<{ checklist_template: { name: string } }>({
    checklist_template: { name: checklist_template.name },
  })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    const payload = { checklist_template: form.data.checklist_template }
    const options = { preserveScroll: true }
    if (checklist_template.id) {
      router.patch(`/settings/checklist_templates/${checklist_template.id}`, payload, options)
    } else {
      router.post("/settings/checklist_templates", payload, options)
    }
  }

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <SettingsLayout wide>
        <div className="flex flex-col gap-6">
          <header>
            <h1>{title}</h1>
            <p className="mt-2">Define the tasks this template creates, then apply it to a client.</p>
          </header>

          {props.flash.notice && (
            <div className="rounded-md border border-accent bg-accent-faded px-4 py-3 text-sm text-accent-display">
              {props.flash.notice}
            </div>
          )}

          <form onSubmit={submit} className="space-y-6">
            <section className="rounded-lg border border-hairline bg-page p-6">
              <div className="max-w-sm space-y-2">
                <label htmlFor="name">Name</label>
                <Input
                  id="name"
                  value={form.data.checklist_template.name}
                  onChange={(event) =>
                    form.setData("checklist_template", { name: event.target.value })
                  }
                />
                {errors.name && (
                  <div className="text-sm text-danger-display">{errors.name}</div>
                )}
              </div>
              <div className="mt-5 flex justify-end">
                <Button type="submit" disabled={form.processing}>
                  Save template
                </Button>
              </div>
            </section>
          </form>

          {checklist_template.id ? (
            <>
              <ItemsSection
                templateId={checklist_template.id}
                items={checklist_template.items}
                errors={errors}
              />
              <ApplySection templateId={checklist_template.id} clients={clients} />
            </>
          ) : (
            <p className="text-sm text-ink-muted">
              Save the template first to add checklist items and apply it to a client.
            </p>
          )}

          <div className="flex justify-start">
            <Button asChild variant="secondary">
              <Link href="/settings/checklist_templates">Back to templates</Link>
            </Button>
          </div>
        </div>
      </SettingsLayout>
    </>
  )
}

function ItemsSection({
  templateId,
  items,
  errors,
}: {
  readonly templateId: number
  readonly items: readonly ChecklistTemplateItem[]
  readonly errors: Record<string, string>
}) {
  const [newTitle, setNewTitle] = React.useState("")
  const [newOffset, setNewOffset] = React.useState("0")
  const [editingId, setEditingId] = React.useState<number | null>(null)
  const [editTitle, setEditTitle] = React.useState("")
  const [editOffset, setEditOffset] = React.useState("0")

  const addItem = (event: FormEvent) => {
    event.preventDefault()
    router.post(
      `/settings/checklist_templates/${templateId}/items`,
      {
        checklist_template_item: {
          title: newTitle,
          due_offset_days: Number(newOffset) || 0,
        },
      },
      {
        preserveScroll: true,
        onSuccess: () => {
          setNewTitle("")
          setNewOffset("0")
        },
      },
    )
  }

  const startEdit = (item: ChecklistTemplateItem) => {
    setEditingId(item.id)
    setEditTitle(item.title)
    setEditOffset(String(item.due_offset_days))
  }

  const saveEdit = (event: FormEvent) => {
    event.preventDefault()
    if (editingId === null) return
    router.patch(
      `/settings/checklist_templates/${templateId}/items/${editingId}`,
      {
        checklist_template_item: {
          title: editTitle,
          due_offset_days: Number(editOffset) || 0,
        },
      },
      {
        preserveScroll: true,
        onSuccess: () => setEditingId(null),
      },
    )
  }

  const removeItem = (itemId: number) => {
    router.delete(`/settings/checklist_templates/${templateId}/items/${itemId}`, {
      preserveScroll: true,
    })
  }

  return (
    <section className="flex flex-col gap-4">
      <h2>Checklist items</h2>

      {items.length > 0 ? (
        <ul className="divide-y divide-hairline overflow-hidden rounded-lg border border-hairline bg-page">
          {items.map((item) =>
            editingId === item.id ? (
              <li key={item.id} className="px-4 py-3">
                <form onSubmit={saveEdit} className="flex flex-wrap items-end gap-3">
                  <div className="min-w-[12rem] flex-1 space-y-2">
                    <label htmlFor={`item-title-${item.id}`}>Title</label>
                    <Input
                      id={`item-title-${item.id}`}
                      value={editTitle}
                      onChange={(event) => setEditTitle(event.target.value)}
                    />
                  </div>
                  <div className="w-32 space-y-2">
                    <label htmlFor={`item-offset-${item.id}`}>Due (days)</label>
                    <Input
                      id={`item-offset-${item.id}`}
                      type="number"
                      value={editOffset}
                      onChange={(event) => setEditOffset(event.target.value)}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" size="sm">Save</Button>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setEditingId(null)}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </li>
            ) : (
              <li key={item.id} className="flex items-center justify-between gap-4 px-4 py-3">
                <div className="min-w-0">
                  <div className="truncate text-ink-display">{item.title}</div>
                  <div className="mt-1 text-sm text-ink-muted">
                    Due {item.due_offset_days} {item.due_offset_days === 1 ? "day" : "days"} after applying
                  </div>
                </div>
                <div className="flex shrink-0 gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label="Edit item"
                    onClick={() => startEdit(item)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label="Remove item"
                    onClick={() => removeItem(item.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </li>
            ),
          )}
        </ul>
      ) : (
        <p className="text-sm text-ink-muted">No items yet — add one below.</p>
      )}

      <form onSubmit={addItem} className="flex flex-wrap items-end gap-3 rounded-lg border border-hairline bg-page p-4">
        <div className="min-w-[12rem] flex-1 space-y-2">
          <label htmlFor="new-item-title">New item title</label>
          <Input
            id="new-item-title"
            value={newTitle}
            onChange={(event) => setNewTitle(event.target.value)}
            required
          />
          {errors.title && <div className="text-sm text-danger-display">{errors.title}</div>}
        </div>
        <div className="w-32 space-y-2">
          <label htmlFor="new-item-offset">Due (days)</label>
          <Input
            id="new-item-offset"
            type="number"
            value={newOffset}
            onChange={(event) => setNewOffset(event.target.value)}
          />
        </div>
        <Button type="submit">
          <Plus className="h-4 w-4" />
          Add item
        </Button>
      </form>
    </section>
  )
}

function ApplySection({
  templateId,
  clients,
}: {
  readonly templateId: number
  readonly clients: readonly ClientOption[]
}) {
  const [clientId, setClientId] = React.useState(clients[0]?.id ? String(clients[0].id) : "")

  const apply = (event: FormEvent) => {
    event.preventDefault()
    if (!clientId) return
    router.post(
      `/settings/checklist_templates/${templateId}/apply`,
      { client_id: clientId },
      { preserveScroll: true },
    )
  }

  return (
    <section className="flex flex-col gap-4">
      <h2>Apply to client</h2>
      {clients.length > 0 ? (
        <form onSubmit={apply} className="flex flex-wrap items-end gap-3">
          <div className="w-64 space-y-2">
            <label htmlFor="apply-client">Client</label>
            <Select
              id="apply-client"
              value={clientId}
              onChange={(event) => setClientId(event.target.value)}
            >
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.display_name}
                </option>
              ))}
            </Select>
          </div>
          <Button type="submit">Apply</Button>
        </form>
      ) : (
        <p className="text-sm text-ink-muted">No clients configured yet.</p>
      )}
    </section>
  )
}
